/**
 * Store do editor.
 *
 * Duas responsabilidades que valem explicação:
 *
 * - **Autosave só do draft.** Toda mutação marca `dirty` e agenda um save com debounce. O que é
 *   salvo é exclusivamente o draft; `published` nunca é tocado daqui (as Rules recusariam, e é
 *   justamente essa recusa que faz o versionamento existir).
 * - **Blockers calculados na hora.** `getBlockers()` chama `collectPublishBlockers` do engine, a
 *   mesma função que a Cloud Function usa para recusar a publicação. O painel não tem uma segunda
 *   lista de regras que pode divergir do servidor.
 */

import { applyEdgeChanges, applyNodeChanges, type Connection, type Edge, type EdgeChange, type NodeChange } from '@xyflow/react';
import { create } from 'zustand';
import {
  collectPublishBlockers,
  createDefaultAppearance,
  sameVersionContent,
  type Appearance,
  type BlockData,
  type BlockKind,
  type WebchatConfig,
  type WebchatVersion,
} from '@merlin/flow-engine';
import { getDataSource, PublishBlockedError, type WebchatDoc } from '../data/index.js';
import {
  connectReplacingHandle,
  createBlockNode,
  duplicateBlockNode,
  fromReactFlow,
  toReactFlowEdges,
  toReactFlowNodes,
  type AnyBlockNode,
} from '../flow/mapping.js';

const AUTOSAVE_DELAY_MS = 800;

/*
 * Aqui morava `InspectorTab`, com as oito abas do painel lateral, e um `select()` que forçava a aba
 * `bloco` a cada clique no canvas. Existia para resolver um problema real — clicar num bloco estando
 * em "Domínios" deixava o bloco destacado e o formulário dele escondido — mas resolvia tratando o
 * sintoma.
 *
 * A causa era a coluna de 384px acumular três coisas de naturezas diferentes: o objeto selecionado,
 * a configuração do webchat e os números. Hoje elas são três telas (`FlowPage`, `SettingsPage`,
 * `DataPage`), o painel lateral só mostra o bloco, e não há aba nenhuma para trazer de volta.
 */

export interface EditorState {
  id: string | null;
  loading: boolean;
  loadError: string | null;

  name: string;
  publicKey: string;
  authorizedDomains: string[];
  outboundAllowlist: string[];
  secretNames: string[];
  /** Ausente no documento conta como ligado — ver `data/types.ts`. */
  collectLeads: boolean;
  /**
   * Teto mensal de conversas. `undefined` = nunca escolhido (vale o padrão), `null` = ilimitado.
   * Guardado cru para o painel distinguir "padrão de 10 mil" de "ilimitado por escolha".
   */
  conversationLimit: number | null | undefined;
  /**
   * Teto mensal de requisições de config — o que de fato gera custo. Mesmos três estados guardados
   * crus, mas aqui **ausente é ilimitado**: ver `resolveRequestLimit` no engine.
   */
  requestLimit: number | null | undefined;
  /**
   * Estado administrativo, **somente leitura**. Não há `setStatus` de propósito: as Rules recusam a
   * escrita, e um setter aqui prometeria algo que o servidor nega.
   */
  status: string | undefined;
  appearance: Appearance;
  nodes: AnyBlockNode[];
  edges: Edge[];
  startNodeId: string;
  publishedVersion: number | null;
  /**
   * Conteúdo que está no ar, guardado para o painel saber se há algo novo a publicar.
   *
   * Antes só a `version` era mantida, e sem o conteúdo não havia como responder "mudou algo?" — o
   * botão Publicar ficava sempre habilitado e cada clique criava uma versão idêntica.
   */
  publishedSnapshot: WebchatVersion | null;

  selectedNodeId: string | null;
  dirty: boolean;
  saving: boolean;
  savedAt: number | null;
  saveError: string | null;
  publishing: boolean;
  publishError: string | null;
  publishBlockers: string[];
  /** Recado neutro, não erro: usado quando publicar não criou versão porque nada mudou. */
  publishNotice: string | null;
  publishedAtLabel: string | null;

  load(id: string): Promise<void>;
  reset(): void;

  onNodesChange(changes: NodeChange<AnyBlockNode>[]): void;
  onEdgesChange(changes: EdgeChange[]): void;
  onConnect(connection: Connection): void;

  addBlock(type: BlockKind, position: { x: number; y: number }): void;
  /** Cópia do bloco, com id novo, sem arestas e já selecionada. `false` = não existe esse nó. */
  duplicateNode(nodeId: string): boolean;
  updateBlock(nodeId: string, patch: Partial<BlockData>): void;
  removeNode(nodeId: string): void;
  /** Tira a opção do bloco de botões e a aresta que saía dela. */
  removeButtonOption(nodeId: string, optionId: string): void;
  removeEdge(edgeId: string): void;
  select(nodeId: string | null): void;
  setStartNode(nodeId: string): void;

  setName(name: string): void;
  setAppearance(patch: Partial<Appearance>): void;
  setAuthorizedDomains(domains: string[]): void;
  setOutboundAllowlist(domains: string[]): void;
  setCollectLeads(enabled: boolean): void;
  setConversationLimit(limit: number | null): void;
  setRequestLimit(limit: number | null): void;

  saveNow(): Promise<void>;
  publish(): Promise<void>;
  restoreVersion(version: number): Promise<void>;
  upsertSecret(name: string, value: string): Promise<void>;
  deleteSecret(name: string): Promise<void>;

  getDraftVersion(): WebchatVersion;
  getPreviewConfig(): WebchatConfig;
  getBlockers(): string[];
  /** `false` quando o rascunho é igual ao que está no ar — publicar não criaria versão. */
  hasUnpublishedChanges(): boolean;
}

let autosaveTimer: ReturnType<typeof setTimeout> | null = null;

function emptyState() {
  return {
    id: null,
    loading: false,
    loadError: null,
    name: '',
    publicKey: '',
    authorizedDomains: [] as string[],
    outboundAllowlist: [] as string[],
    secretNames: [] as string[],
    collectLeads: true,
    conversationLimit: undefined as number | null | undefined,
    requestLimit: undefined as number | null | undefined,
    status: undefined as string | undefined,
    appearance: createDefaultAppearance(),
    nodes: [] as AnyBlockNode[],
    edges: [] as Edge[],
    startNodeId: '',
    publishedVersion: null,
    publishedSnapshot: null as WebchatVersion | null,
    selectedNodeId: null,
    dirty: false,
    saving: false,
    savedAt: null,
    saveError: null,
    publishing: false,
    publishError: null,
    publishBlockers: [] as string[],
    publishNotice: null,
    publishedAtLabel: null,
  };
}

export const useEditor = create<EditorState>((set, get) => {
  /** Marca sujo e agenda o save. Chamado por toda mutação de conteúdo. */
  function touch(): void {
    set({ dirty: true, saveError: null });
    if (autosaveTimer) clearTimeout(autosaveTimer);
    autosaveTimer = setTimeout(() => {
      void get().saveNow();
    }, AUTOSAVE_DELAY_MS);
  }

  function applyDoc(doc: WebchatDoc): void {
    set({
      id: doc.id,
      loading: false,
      loadError: null,
      name: doc.name,
      publicKey: doc.publicKey,
      authorizedDomains: doc.authorizedDomains,
      outboundAllowlist: doc.outboundAllowlist,
      secretNames: doc.secretNames,
      collectLeads: doc.collectLeads !== false,
      conversationLimit: doc.conversationLimit,
      requestLimit: doc.requestLimit,
      status: doc.status,
      appearance: doc.draft.appearance,
      nodes: toReactFlowNodes(doc.draft.flow),
      edges: toReactFlowEdges(doc.draft.flow),
      startNodeId: doc.draft.flow.startNodeId,
      publishedVersion: doc.published?.version ?? null,
      publishedSnapshot: doc.published
        ? { appearance: doc.published.appearance, flow: doc.published.flow }
        : null,
      selectedNodeId: null,
      dirty: false,
      savedAt: doc.updatedAt,
      publishBlockers: [],
      publishError: null,
      publishNotice: null,
    });
  }

  return {
    ...emptyState(),

    async load(id: string): Promise<void> {
      set({ loading: true, loadError: null });
      try {
        const doc = await getDataSource().get(id);
        if (!doc) {
          set({ loading: false, loadError: 'Webchat não encontrado.' });
          return;
        }
        applyDoc(doc);
      } catch (error) {
        set({ loading: false, loadError: error instanceof Error ? error.message : String(error) });
      }
    },

    reset(): void {
      if (autosaveTimer) clearTimeout(autosaveTimer);
      autosaveTimer = null;
      set(emptyState());
    },

    onNodesChange(changes): void {
      set({ nodes: applyNodeChanges(changes, get().nodes) });
      // Só posição/remoção mexem no conteúdo salvo; seleção e hover não devem sujar o draft.
      if (changes.some((change) => change.type === 'position' || change.type === 'remove')) touch();
      const removed = changes.find((change) => change.type === 'remove');
      if (removed && get().selectedNodeId === (removed as { id: string }).id) {
        set({ selectedNodeId: null });
      }
      // Se o nó inicial foi apagado, elege o primeiro que sobrou — fluxo sem início não publica.
      const state = get();
      if (state.startNodeId && !state.nodes.some((node) => node.id === state.startNodeId)) {
        set({ startNodeId: state.nodes[0]?.id ?? '' });
      }
    },

    onEdgesChange(changes): void {
      set({ edges: applyEdgeChanges(changes, get().edges) });
      if (changes.some((change) => change.type === 'remove' || change.type === 'add')) touch();
    },

    onConnect(connection): void {
      set({ edges: connectReplacingHandle(get().edges, connection) });
      touch();
    },

    addBlock(type, position): void {
      const node = createBlockNode(type, position);
      const state = get();
      set({
        nodes: [...state.nodes, node],
        selectedNodeId: node.id,
        // Primeiro bloco do fluxo vira o início automaticamente.
        startNodeId: state.nodes.length === 0 ? node.id : state.startNodeId,
      });
      touch();
    },

    /**
     * Duplica o bloco selecionado.
     *
     * O `startNodeId` **não** muda: a cópia nunca vira o início, mesmo que o original seja. Herdar isso
     * mudaria por onde a conversa começa por causa de um clique em "Duplicar", que é o oposto do que a
     * palavra promete.
     */
    duplicateNode(nodeId): boolean {
      const state = get();
      const original = state.nodes.find((node) => node.id === nodeId);
      if (!original) return false;

      const copia = duplicateBlockNode(original);
      set({ nodes: [...state.nodes, copia], selectedNodeId: copia.id });
      touch();
      return true;
    },

    updateBlock(nodeId, patch): void {
      set({
        nodes: get().nodes.map((node) =>
          node.id === nodeId
            ? { ...node, data: { block: { ...node.data.block, ...patch } as BlockData } }
            : node,
        ),
      });
      touch();
    },

    removeNode(nodeId): void {
      const state = get();
      const nodes = state.nodes.filter((node) => node.id !== nodeId);
      set({
        nodes,
        edges: state.edges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId),
        selectedNodeId: state.selectedNodeId === nodeId ? null : state.selectedNodeId,
        startNodeId: state.startNodeId === nodeId ? (nodes[0]?.id ?? '') : state.startNodeId,
      });
      touch();
    },

    /**
     * Remove uma opção de bloco de botões **e a aresta que saía dela**.
     *
     * Mora aqui, e não no formulário, porque só o store tem as duas coleções. Antes o formulário
     * apenas filtrava o array de opções: a aresta continuava no documento apontando para uma porta
     * que não existia mais — invisível no canvas, e publicada assim mesmo.
     */
    removeButtonOption(nodeId, optionId): void {
      const state = get();
      set({
        nodes: state.nodes.map((node) => {
          if (node.id !== nodeId) return node;
          const bloco = node.data.block as { options?: { id: string }[] };
          const options = (bloco.options ?? []).filter((option) => option.id !== optionId);
          return { ...node, data: { block: { ...bloco, options } as BlockData } };
        }),
        edges: state.edges.filter(
          (edge) => !(edge.source === nodeId && edge.sourceHandle === optionId),
        ),
      });
      touch();
    },

    /** Usado pelo ✕ da aresta. Só a conexão sai; os blocos das duas pontas ficam onde estão. */
    removeEdge(edgeId): void {
      set({ edges: get().edges.filter((edge) => edge.id !== edgeId) });
      touch();
    },

    select(nodeId): void {
      set({ selectedNodeId: nodeId });
    },

    setStartNode(nodeId): void {
      set({ startNodeId: nodeId });
      touch();
    },

    setName(name): void {
      set({ name });
      touch();
    },

    setAppearance(patch): void {
      set({ appearance: { ...get().appearance, ...patch } });
      touch();
    },

    setAuthorizedDomains(domains): void {
      set({ authorizedDomains: domains });
      touch();
    },

    setOutboundAllowlist(domains): void {
      set({ outboundAllowlist: domains });
      touch();
    },

    setCollectLeads(enabled): void {
      set({ collectLeads: enabled });
      touch();
    },

    /** `null` = ilimitado. Ver `resolveConversationLimit` para os três estados. */
    setConversationLimit(limit): void {
      set({ conversationLimit: limit });
      touch();
    },

    /** `null` = ilimitado. Diferente do de conversas, ausente também é ilimitado. */
    setRequestLimit(limit): void {
      set({ requestLimit: limit });
      touch();
    },

    async saveNow(): Promise<void> {
      const state = get();
      if (!state.id || !state.dirty) return;
      set({ saving: true, saveError: null });
      try {
        await getDataSource().saveDraft(state.id, {
          name: state.name,
          authorizedDomains: state.authorizedDomains,
          outboundAllowlist: state.outboundAllowlist,
          collectLeads: state.collectLeads,
          conversationLimit: state.conversationLimit,
          requestLimit: state.requestLimit,
          appearance: state.appearance,
          flow: fromReactFlow(state.nodes, state.edges, state.startNodeId),
        });
        set({ saving: false, dirty: false, savedAt: Date.now() });
      } catch (error) {
        set({ saving: false, saveError: error instanceof Error ? error.message : String(error) });
      }
    },

    async publish(): Promise<void> {
      const state = get();
      if (!state.id) return;

      // Publicar sempre parte do que está salvo: sem isto, a edição dos últimos 800ms ficaria fora.
      if (state.dirty) await get().saveNow();
      if (get().saveError) {
        set({ publishError: 'Não foi possível salvar o draft antes de publicar.' });
        return;
      }

      set({ publishing: true, publishError: null, publishBlockers: [], publishNotice: null });
      try {
        const result = await getDataSource().publish(state.id);
        /*
         * `unchanged` chega quando o servidor não criou versão porque o conteúdo é idêntico. O botão
         * já fica desabilitado nesse caso, então isto cobre a corrida — duas abas, ou publicar de
         * outro lugar entre o carregamento e o clique.
         */
        set({
          publishing: false,
          publishedVersion: result.version,
          publishBlockers: [],
          publishNotice: result.unchanged
            ? `Nada mudou desde a v${result.version}: nenhuma versão nova foi criada.`
            : null,
          // O que está no ar passa a ser o que acabou de subir — sem isto o botão continuaria ativo.
          publishedSnapshot: result.unchanged ? get().publishedSnapshot : get().getDraftVersion(),
        });
      } catch (error) {
        if (error instanceof PublishBlockedError) {
          set({ publishing: false, publishBlockers: error.blockers, publishError: error.message });
          return;
        }
        set({
          publishing: false,
          publishError: error instanceof Error ? error.message : String(error),
        });
      }
    },

    /**
     * Traz uma versão publicada de volta **para o rascunho**, nunca direto para o ar.
     *
     * Três motivos: a republicação continua passando pela validação server-side que já existe, o
     * usuário revisa antes, e nada muda para os visitantes sem um clique consciente em Publicar. As
     * Rules, aliás, recusariam uma escrita do cliente em `published`.
     */
    async restoreVersion(version): Promise<void> {
      const state = get();
      if (!state.id) return;

      const conteudo = await getDataSource().getVersion(state.id, version);
      if (!conteudo) {
        set({ saveError: `Não encontrei o conteúdo da versão ${version}.` });
        return;
      }

      set({
        appearance: conteudo.appearance,
        nodes: toReactFlowNodes(conteudo.flow),
        edges: toReactFlowEdges(conteudo.flow),
        startNodeId: conteudo.flow.startNodeId,
        // O bloco selecionado pode não existir na versão restaurada.
        selectedNodeId: null,
        publishBlockers: [],
        publishError: null,
      });
      touch();
    },

    async upsertSecret(name, value): Promise<void> {
      const state = get();
      if (!state.id) return;
      await getDataSource().upsertSecret(state.id, name, value);
      if (!state.secretNames.includes(name)) {
        set({ secretNames: [...state.secretNames, name] });
      }
    },

    async deleteSecret(name): Promise<void> {
      const state = get();
      if (!state.id) return;
      await getDataSource().deleteSecret(state.id, name);
      set({ secretNames: state.secretNames.filter((item) => item !== name) });
    },

    getDraftVersion(): WebchatVersion {
      const state = get();
      return {
        appearance: state.appearance,
        flow: fromReactFlow(state.nodes, state.edges, state.startNodeId),
      };
    },

    /*
     * Nunca publicado conta como "tem mudança": é a primeira publicação, e ela precisa acontecer.
     *
     * Usa `sameVersionContent` do engine, a mesma função que a Cloud Function usa para decidir se
     * grava. Uma segunda regra aqui poderia divergir, e o sintoma seria o pior possível: botão
     * habilitado que não faz nada, ou desabilitado com alteração de verdade parada no rascunho.
     */
    hasUnpublishedChanges(): boolean {
      const snapshot = get().publishedSnapshot;
      if (!snapshot) return true;
      return !sameVersionContent(snapshot, get().getDraftVersion());
    },

    /** Config sintética para o preview: mesmo tipo que o SDK recebe em produção. */
    getPreviewConfig(): WebchatConfig {
      const state = get();
      return {
        publicKey: state.publicKey || 'pk_preview',
        name: state.name,
        authorizedDomains: state.authorizedDomains.length ? state.authorizedDomains : ['localhost'],
        ...get().getDraftVersion(),
        version: state.publishedVersion ?? 0,
      };
    },

    getBlockers(): string[] {
      const state = get();
      return collectPublishBlockers(get().getDraftVersion(), state.authorizedDomains);
    },
  };
});
